import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const MODES = [
  { key: "day",       label: "Single Day" },
  { key: "week",      label: "Week (7 days)" },
  { key: "month",     label: "Month" },
  { key: "year",      label: "Full Year" },
  { key: "mahadasha", label: "Mahadasha" },
];

/**
 * CascadeRangePicker — flexible range downloader for the daily cascade PDF.
 * Modes: day / week / month / year / mahadasha
 */
export default function CascadeRangePicker({ profile }) {
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState("day");
  const [date, setDate] = useState(today);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [mahaIndex, setMahaIndex] = useState(0);
  const [mahadashas, setMahadashas] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [loadingMd, setLoadingMd] = useState(false);

  // Compute available year range from native's DOB
  const dobYear = profile ? parseInt(profile.dob.slice(0, 4), 10) : 2000;
  const yearOptions = [];
  for (let y = dobYear; y < dobYear + 120; y++) yearOptions.push(y);

  // Load mahadasha list when mode switches to mahadasha
  const loadMd = useCallback(async () => {
    if (mode !== "mahadasha" || mahadashas.length > 0) return;
    setLoadingMd(true);
    try {
      const r = await api.mahadashaList(profile.profile_id);
      setMahadashas(r.mahadashas);
    } catch (err) {
      console.error("mahadasha list failed:", err);
      toast.error("Failed to load mahadasha list");
    }
    setLoadingMd(false);
  }, [mode, mahadashas.length, profile?.profile_id]);
  useEffect(() => { loadMd(); }, [loadMd]);

  const buildPayload = () => {
    if (mode === "day")   return { mode, date };
    if (mode === "week")  return { mode, date };
    if (mode === "month") return { mode, year: parseInt(year, 10), month: parseInt(month, 10) };
    if (mode === "year")  return { mode, year: parseInt(year, 10) };
    if (mode === "mahadasha") return { mode, maha_index: parseInt(mahaIndex, 10) };
    return {};
  };

  const download = async () => {
    if (!profile) return;
    setDownloading(true);
    try {
      const res = await fetch(api.cascadeRangePdfUrl(profile.profile_id), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Download failed" }));
        throw new Error(err.detail || "Download failed");
      }
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      // Match backend filename hint
      const md = mahadashas[mahaIndex];
      const label = mode === "day"       ? `day_${date}`
                  : mode === "week"      ? `week_${date}`
                  : mode === "month"     ? `month_${year}_${String(month).padStart(2, "0")}`
                  : mode === "year"      ? `year_${year}`
                  : mode === "mahadasha" ? `mahadasha_${mahaIndex}_${md?.lord ?? ""}`
                  : "range";
      link.download = `cascade_${profile.name.replace(/\s+/g, "_")}_${label}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("cascade range download failed:", err);
      toast.error(err.message || "Download failed");
    }
    setDownloading(false);
  };

  const modeTiming = {
    day: "instant · 1 page",
    week: "~2 sec · 2-3 pages",
    month: "~5 sec · 8-12 pages",
    year: "~30 sec · ~120 pages",
    mahadasha: "up to 15 min for long mahadashas (Saturn/Venus)",
  };

  return (
    <div className="space-y-3" data-testid="cascade-range-picker">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono mb-2">Range Type</div>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="cascade-mode-select"
                           className="w-52 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
              {MODES.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {mode === "day" && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono mb-2">Date</div>
            <Input data-testid="cascade-day-date" type="date" value={date}
                   onChange={(e) => setDate(e.target.value)}
                   className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono w-44" />
          </div>
        )}

        {mode === "week" && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono mb-2">Week Starting</div>
            <Input data-testid="cascade-week-date" type="date" value={date}
                   onChange={(e) => setDate(e.target.value)}
                   className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono w-44" />
          </div>
        )}

        {(mode === "month" || mode === "year") && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono mb-2">Year</div>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
              <SelectTrigger data-testid="cascade-year-select"
                             className="w-32 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6] max-h-64">
                {yearOptions.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {mode === "month" && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono mb-2">Month</div>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
              <SelectTrigger data-testid="cascade-month-select"
                             className="w-40 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6] max-h-64">
                {MONTHS.map((mname, i) => (
                  <SelectItem key={mname} value={String(i + 1)}>{mname}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {mode === "mahadasha" && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono mb-2">Mahadasha</div>
            <Select value={String(mahaIndex)} onValueChange={(v) => setMahaIndex(parseInt(v, 10))}
                    disabled={loadingMd}>
              <SelectTrigger data-testid="cascade-maha-select"
                             className="w-72 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
                <SelectValue placeholder={loadingMd ? "Loading…" : "Pick a mahadasha"} />
              </SelectTrigger>
              <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
                {mahadashas.map(md => (
                  <SelectItem key={md.index} value={String(md.index)}>
                    <span className="font-heading">{md.lord}</span>
                    <span className="text-[#6E6B64] text-xs ml-2 font-mono">
                      {md.start} → {md.end} ({md.years}y)
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Button data-testid="cascade-range-download-btn" onClick={download} disabled={downloading}
                className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">
          {downloading
            ? <><Loader2 size={14} className="mr-2 animate-spin" /> Building PDF…</>
            : <><Download size={14} className="mr-2" /> Download PDF</>}
        </Button>
      </div>

      <div className="text-[10px] text-[#6E6B64] font-mono">
        <span className="text-[#A8896C]">Selected:</span> {MODES.find(m => m.key === mode).label} · {modeTiming[mode]}
      </div>
    </div>
  );
}
